import { AccessRequest, authenticate, checkAuth, continueChat, EmailMessage, FullTicker, getAccessRequests, getAllNews, getBalanceSheetSummary, getCashFlowStatementSummary, getChatTitle, getIncomeStatementSummary, getLatestSummaries, getNotSignedUp, getStockList, getSubscriptionInfo, isFailedResponse, LatestSummary, requestAccess, sendChat, sendEmail, signOut, signUp, startEvents, SubLevel, SubscriptionInfo, SubscriptionInfoResponse } from '@/utils/api'
import { BrowserCache } from '@/utils/cache'
import { Commit, createStore } from 'vuex'

const cache = new BrowserCache(false)

export interface ChatInteraction {
  user: string
  message: string
  timestamp: number
}

export interface ChatHistory {
  id: string
  username: string
  timestamp: number
  updatedAt: number
  title: string
  symbol: string
  history: ChatInteraction[]
}

/**
 * steps
 * assetType,
 * assetData,
 * calendarEvents,
    newsSentiments,
    incomeStatement,
    cashflowStatement,
    balanceSheet,
    companySummary,
    insights,
    quote,
    analysis,
    analysisSummary
 */


export type StepsMap = Record<string, { completed: boolean, started: number, finished: number }>

export interface UpdateEvent {
  type: 'updateSteps'
  payload: StepsMap
}

const subNameLookup: Record<SubLevel, string> = {
  0: 'Trial',
  1: 'Basic',
  2: 'Premium',
  3: 'Pro',
  4: 'Extreme',
}

export interface SubscriptionLocal {
  active: boolean
  level: string
  expires: number
}

export interface EmailTemplate {
  from: string
  subject: string
  html: string
  text: string
}

// export interface EmailState {
//   templates: Map<string, EmailTemplate>
// }

// const emailState: EmailState = {
//   templates: new Map<string, EmailTemplate>()
// }


export interface GlobalState {
  stripePricingTableId: string,
  stripePublishableKey: string,
  username: string
  isAdmin: boolean
  demoModeDisplay: boolean
  firstname: string
  lastname: string
  email: string
  rememberUser: boolean
  userReferralCode: string
  authenticated: boolean | null
  chatHistory: ChatHistory[]
  showOptions: boolean
  userContext: Record<string, string>
  stocks: Record<string, FullTicker>
  steps: Record<string, StepsMap>
  snackbarVisible: boolean
  snackbarMessage: string
  snackbarIcon: string
  remainingQuota: number
  subscriptionInfo: SubscriptionLocal | null
  accessRequests: AccessRequest[]
  latestSummaries: LatestSummary[]
  emailTemplates?: Record<string, EmailTemplate>
}

const PACKAGE_VERSION = process.env.PACKAGE_VERSION
const VUE_APP_ENV = process.env.VUE_APP_ENV
const LIVE_PRICING_TABLE_ID = process.env.LIVE_PRICING_TABLE_ID
const LIVE_PUB_KEY = process.env.LIVE_PUB_KEY
const TEST_PRICING_TABLE_ID = process.env.TEST_PRICING_TABLE_ID
const TEST_PUB_KEY = process.env.TEST_PUB_KEY
const stripePricingTableId = VUE_APP_ENV === 'production' ? LIVE_PRICING_TABLE_ID : TEST_PRICING_TABLE_ID
const stripePublishableKey = VUE_APP_ENV === 'production' ? LIVE_PUB_KEY : TEST_PUB_KEY

const globalState: GlobalState = {
  stripePricingTableId,
  stripePublishableKey,
  username: 'unknown',
  isAdmin: false,
  demoModeDisplay: false,
  firstname: '',
  lastname: '',
  email: '',
  rememberUser: false,
  userReferralCode: '',
  authenticated: false,
  chatHistory: [],
  showOptions: false,
  userContext: {},
  stocks: {},
  steps: {},
  snackbarVisible: false,
  snackbarMessage: '',
  snackbarIcon: 'mdi-thumb-up',
  remainingQuota: 3,
  subscriptionInfo: null,
  accessRequests: [],
  latestSummaries: [],
  emailTemplates: undefined
}

const initialCacheState: { global: GlobalState } = {
  global: cache.get('global') as GlobalState ?? globalState,
  // email: cache.get('email') as EmailState ?? emailState
}
if (initialCacheState.global.remainingQuota == null) {
  initialCacheState.global.remainingQuota = 3
}
if (initialCacheState.global.subscriptionInfo === undefined) {
  initialCacheState.global.subscriptionInfo = null
}
if (initialCacheState.global.snackbarVisible) {
  initialCacheState.global.snackbarVisible = false
}
initialCacheState.global.stripePricingTableId = stripePricingTableId
initialCacheState.global.stripePublishableKey = stripePublishableKey


async function authAndSub() {
  const { success, message, username, first, last, email } = await checkAuth()
  if (success && username != null) {
    const response = await getSubscriptionInfo(username)
    const { success: subSuccess, ...info } = response
    if (subSuccess && info != null && Object.keys(info).length > 0) {
      return { success, username, first, last, email, info: info as SubscriptionInfo }
    }
  }
  return { success, message, username, first, last, email, info: null }
}

function prepCache(state: GlobalState) {
  const {
    authenticated,
    showOptions,
    snackbarVisible,
    stripePricingTableId,
    stripePublishableKey,
    remainingQuota,
    isAdmin,
    demoModeDisplay,
    emailTemplates,
    latestSummaries,
    ...remaining
  } = state
  return isAdmin ? {
    ...remaining,
    isAdmin: false,
    demoModeDisplay: false,
    authenticated: null,
    showOptions: false,
    snackbarVisible: false,
    latestSummaries: [],
    emailTemplates
  } : {
    ...remaining,
    isAdmin: false,
    demoModeDisplay: false,
    authenticated: null,
    showOptions: false,
    snackbarVisible: false,
    latestSummaries: []
  }
}

export default createStore({
  state: initialCacheState.global,
  getters: {
    userChatHistory(state: GlobalState) {
      return (state.chatHistory ?? []).filter(chat => chat.username === state.username)
    },
    contextForUser(state: GlobalState) {
      const { username } = state
      return (state.userContext ?? {})[username] ?? ''
    },
    stockTickerList(state: GlobalState) {
      return Object.keys((state.stocks ?? {}))
    },
    stockFullTickerList(state: GlobalState) {
      return Object.values((state.stocks ?? {}))
    }
  },
  mutations: {
    setAuthenticated(state: GlobalState, { username, authenticated, first, last, email }: { username: string, authenticated: boolean, first: string, last: string, email: string }) {
      state.authenticated = authenticated
      state.username = !authenticated && state.rememberUser ? state.username : username
      state.firstname = first
      state.lastname = last
      state.email = email
      state.isAdmin = username === 'j4ys0n'
      cache.add('global', prepCache(state))

    },
    toggleDemoMode(state: GlobalState) {
      state.demoModeDisplay = !state.demoModeDisplay
    },
    saveChatHistory(state: GlobalState, chatHistory: ChatHistory) {
      // check if the chat history already exists by searching for id in state.chatHistory
      if (chatHistory.symbol == null) {
        chatHistory.symbol = chatHistory.title
      }
      state.chatHistory == null ? state.chatHistory = [] : null
      const index = state.chatHistory.findIndex(history => history.id === chatHistory.id)
      console.log(index)
      if (index !== -1) {
        // replace chat history in state.chatHistory
        state.chatHistory[index] = chatHistory
        cache.add('global', prepCache(state))
      } else {
        // push new
        state.chatHistory.push(chatHistory)
        cache.add('global', prepCache(state))
      }
    },
    deleteChat(state: GlobalState, chatId: string) {
      state.chatHistory = state.chatHistory.filter(chat => chat.id !== chatId)
      cache.add('global', prepCache(state))
    },
    toggleOptions(state: GlobalState) {
      state.showOptions = !state.showOptions
    },
    updateUserContext(state: GlobalState, userContext: string) {
      const { username } = state
      if (typeof state.userContext === 'string') {
        state.userContext = {}
      }
      state.userContext[username] = userContext
      cache.add('global', prepCache(state))
    },
    setStocks(state: GlobalState, stocks: Record<string, FullTicker>) {
      state.stocks = stocks
    },
    updateSteps(state: GlobalState, {id, steps}: { id: string, steps: StepsMap }) {
      if (state.steps == null) {
        state.steps = {}
      }
      state.steps[id] = steps
      cache.add('global', prepCache(state))
    },
    showSnackbar(state: GlobalState, { message, icon }: { message: string, icon: string }) {
      state.snackbarVisible = true
      state.snackbarMessage = message
      state.snackbarIcon = icon
    },
    hideSnackbar(state: GlobalState) {
      state.snackbarVisible = false
    },
    setRemainingQuota(state: GlobalState, remainingQuota: number) {
      state.remainingQuota = remainingQuota
    },
    setSubscriptionInfo(state: GlobalState, info: SubscriptionInfo) {
      const { subscriptionActive, subscriptionLevel, subscriptionExpiresAt, remainingQuota } = info
      const subInfo: SubscriptionLocal = {
        active: subscriptionActive ?? false,
        level: subNameLookup[subscriptionLevel ?? 0],
        expires: subscriptionExpiresAt ?? 0
      }
      state.subscriptionInfo = subInfo
      state.remainingQuota = remainingQuota
    },
    setAccessRequests(state: GlobalState, requests: AccessRequest[]) {
      state.accessRequests = requests
    },
    setEmailTemplate(state: GlobalState, { name, template }: { name: string, template: EmailTemplate }) {
      if (state.isAdmin) {
        if (state.emailTemplates == null) {
          state.emailTemplates = {} as Record<string, EmailTemplate>
        }
        state.emailTemplates[name] = template
        cache.add('global', prepCache(state))
      }
    },
    setRememberUser(state: GlobalState, remember: boolean) {
      state.rememberUser = remember
      cache.add('global', prepCache(state))
    },
    setLatestSummaries(state: GlobalState, summaries: LatestSummary[]) {
      state.latestSummaries = summaries
    },
  },
  actions: {
    async authenticate({ commit }: { commit: Commit }, { username, password, remember }: { username: string, password: string, remember: boolean }) {
      const { success, message } = await authenticate({ username, password })
      // console.log({ username, authenticated: success, message })
      commit('setAuthenticated', { username, authenticated: success })
      if (success) {
        const { success: authSuccess, message, username, first, last, email, info } = await authAndSub()
        commit('setAuthenticated', { username, authenticated: authSuccess, first, last, email })
        if (authSuccess && info != null && Object.keys(info).length > 0) {
          commit('setSubscriptionInfo', info as SubscriptionInfo)
        }
      }
      if (!success) {
        commit('showSnackbar', { message, icon: 'mdi-account-alert' })
      }
      if (success) {
        commit('setRememberUser', remember)
        commit('showSnackbar', { message: 'Welcome back!', icon: 'mdi-account-check' })
      }
      return { success, message: message ?? 'Success!' }
    },
    async checkAuth({ commit }: { commit: Commit }) {
      const { success, message, username, first, last, email, info } = await authAndSub()
      // console.log({ username, success, message })
      commit('setAuthenticated', { username, authenticated: success, first, last, email })
      if (success && info != null && Object.keys(info).length > 0) {
        commit('setSubscriptionInfo', info as SubscriptionInfo)
      }
      return { success, message: message ?? 'Success!' }
    },
    async register({ commit }: { commit: Commit }, { username, first, last, password, email, code }) {
      const { success, message } = await signUp({ username, first, last, password, email, code })
      if (success) {
        commit('setAuthenticated', { username, authenticated: false })
        const { success: authSuccess, message: authMessage } = await authenticate({ username, password })
        if (authSuccess) {
          const { success: authSuccess2, message: message2, username, first, last, email, info } = await authAndSub()
          commit('setAuthenticated', { username, authenticated: authSuccess2, first, last, email })
          if (authSuccess2 && info != null && Object.keys(info).length > 0) {
            commit('setSubscriptionInfo', info as SubscriptionInfo)
          }
          if (authSuccess2) {
            return { success: authSuccess, message: authMessage ?? 'Success!' }
          }
        }
      }
      if (!success) {
        commit('showSnackbar', { message, icon: 'mdi-account-alert' })
      }
      return { success, message }
    },
    async signOut({ commit }: { commit: Commit }) {
      await signOut()
      commit('setAuthenticated', { username: '', authenticated: false })
    },
    toggleDemoMode({ commit }: { commit: Commit }) {
      commit('toggleDemoMode')
    },
    async showSnackbar({ commit }: { commit: Commit }, { message, icon = 'mdi-thumb-up' }: { message: string, icon?: string }) {
      commit('showSnackbar', { message, icon })
    },
    async hideSnackbar({ commit }: { commit: Commit }) {
      commit('hideSnackbar')
    },
    async sendChat({ commit }: { commit: Commit }, { input, id }: { input: string, id: string }) {
      const { success, response, message, analysis, remainingQuota } = await sendChat(input, id)
      if (remainingQuota != null && typeof remainingQuota === 'number') {
        commit('setRemainingQuota', remainingQuota)
      }
      return { success, response, message, analysis }
    },
    async saveChatHistory({ commit }: { commit: Commit }, history: ChatHistory) {
      // await saveChatHistoryToDB(history)
      console.log(history)
      commit('saveChatHistory', history)
    },
    async continueChat({ commit }: { commit: Commit }, { input, id }: { input: string, id: string }) {
      const { success, response, message } = await continueChat(input, id)
      return { success, response, message }
    },
    async getChatTitle({ commit }: { commit: Commit }, { input, id }: { input: string, id: string }) {
      const { success, response } = await getChatTitle(input, id)
      return { success, response }
    },
    async deleteChat({ commit }: { commit: Commit }, { id }: { id: string }) {
      // const { success, message } = await deleteChat(id)
      // if (success) {
      //   commit('deleteChat', id)
      // }
      commit('deleteChat', id)
      // return { success, message }
    },
    async getStockList({ commit }: { commit: Commit }) {
      const { success, response } = await getStockList()
      if (success) {
        commit('setStocks', response)
      }
      return { success, response }
    },
    async toggleOptions({ commit }: { commit: Commit }) {
      commit('toggleOptions')
    },
    async updateUserContext({ commit }: { commit: Commit }, { context }: { context: string }) {
      commit('updateUserContext', context)
    },
    async request( { commit }: { commit: Commit }, { email, referralCode }: { email: string, referralCode: string }) {
      const response = await requestAccess({email, referralCode})
      return response
    },
    async getAccessRequests({ commit }: { commit: Commit }) {
      const response = await getAccessRequests()
      if (isFailedResponse(response)) {
        console.warn(response.message)
        commit('showSnackbar', { message: response.message, icon: 'mdi-account-alert' })
        return []
      }
      if (response.requests) {
        commit('setAccessRequests', response.requests)
      }
      return response.requests ?? []
    },
    async getNotSignedUp({ commit }: { commit: Commit }) {
      const response = await getNotSignedUp()
      if (isFailedResponse(response)) {
        console.warn(response.message)
        commit('showSnackbar', { message: response.message, icon: 'mdi-account-alert' })
        return []
      }
      if (response.requests) {
        commit('setAccessRequests', response.requests)
      }
      return response.requests ?? []
    },
    async setEmailTemplate({ commit }: { commit: Commit }, { name, template }: { name: string, template: EmailTemplate }) {
      commit('setEmailTemplate', { name, template })
    },
    async sendEmail({ commit }: { commit: Commit }, { emailMessage }: { emailMessage: EmailMessage }) {
      const response = await sendEmail(emailMessage)
      if (isFailedResponse(response)) {
        console.warn(response.message)
        commit('showSnackbar', { message: `Failed to send email.`, icon: 'mdi-account-alert' })
        return { success: false }
      }
      return response
    },
    subscribeEvents({ commit }: { commit: Commit }, { id }: { id: string }) {
      const eventSource = startEvents(id)
      eventSource.onmessage = (message: MessageEvent) => {
        try {
          const data: UpdateEvent = JSON.parse(message.data)
          if (data.type === 'updateSteps') {
            console.log(data.payload)
            commit('updateSteps', { id, steps: data.payload})
          }

        } catch (error) {
          console.log(message.data)
          console.error('Error parsing event data:', error)
        }
      }
    },
    async getIncomeStatementSummary({ commit }: { commit: Commit }, { id, symbol, timestamp }: { id: string, symbol: string, timestamp: number }) {
      const response = await getIncomeStatementSummary(id, symbol, timestamp)
      return response
    },
    async getCashFlowStatementSummary({ commit }: { commit: Commit }, { id, symbol, timestamp }: { id: string, symbol: string, timestamp: number }) {
      const response = await getCashFlowStatementSummary(id, symbol, timestamp)
      return response
    },
    async getBalanceSheetSummary({ commit }: { commit: Commit }, { id, symbol, timestamp }: { id: string, symbol: string, timestamp: number }) {
      const response = await getBalanceSheetSummary(id, symbol, timestamp)
      return response
    },
    async getAllNews( { commit }: { commit: Commit }, { id, symbol }: { id: string, symbol: string }) {
      const response = await getAllNews(id, symbol)
      return response
    },
    async getLatestSummaries({ commit }: { commit: Commit }, { count }: { count?: number }) {
      const response = await getLatestSummaries()
      if (response != null) {
        commit('setLatestSummaries', response)
      }
      return response
    },
  }
})
